/**
 * "Try it" — type sample answers and watch the rules decide, using the same
 * algorithm the worker runs (qualify.ts). Shows the unsaved draft's result, so
 * an admin can tune rules before saving.
 */
import { useId, useState } from 'react';
import { qualify } from './qualify';
import { qualificationResultMeta } from './screening-format';
import type { ScreeningConfig, ScreeningQuestion } from './types';

const input = 'mt-1 block min-h-10 w-full rounded-md border border-gray-300 bg-white px-2.5 text-sm text-gray-900';

function AnswerInput({ q, id, value, onChange }: { q: ScreeningQuestion; id: string; value: string; onChange: (v: string) => void }) {
  if (q.type === 'boolean') {
    return (
      <select id={id} value={value} onChange={(e) => onChange(e.target.value)} className={input}>
        <option value="">Not answered</option>
        <option value="yes">Yes</option>
        <option value="no">No</option>
      </select>
    );
  }
  if (q.type === 'choice' && (q.choices ?? []).some((c) => c.trim())) {
    return (
      <select id={id} value={value} onChange={(e) => onChange(e.target.value)} className={input}>
        <option value="">Not answered</option>
        {(q.choices ?? []).filter((c) => c.trim()).map((c) => <option key={c} value={c}>{c}</option>)}
      </select>
    );
  }
  return (
    <input id={id} type={q.type === 'number' ? 'number' : 'text'} inputMode={q.type === 'number' ? 'decimal' : undefined} step="any" value={value} onChange={(e) => onChange(e.target.value)} placeholder={q.type === 'number' ? '0' : 'Sample answer'} className={input} />
  );
}

export function TryItPanel({ config }: { config: ScreeningConfig }) {
  const id = useId();
  const [sample, setSample] = useState<Record<string, string>>({});
  const questions = config.questions.filter((q) => q.key);
  const answers: Record<string, unknown> = {};
  for (const q of questions) if (sample[q.key] !== undefined && sample[q.key] !== '') answers[q.key] = sample[q.key];
  const touched = Object.keys(answers).length > 0;
  const result = qualify(config, answers);
  const meta = qualificationResultMeta(result.result);

  return (
    <section aria-labelledby={`${id}-h`} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm" data-testid="try-it">
      <h2 id={`${id}-h`} className="text-[15px] font-semibold text-gray-900">Try it</h2>
      <p className="mt-0.5 text-[13px] text-muted">Answer as a candidate might. The result uses the rules above as they are right now, saved or not.</p>
      {questions.length === 0 ? (
        <p className="mt-3 text-[13px] text-muted">Add a question to try the rules.</p>
      ) : (
        <div className="mt-3 grid gap-3 lg:grid-cols-[1fr_minmax(16rem,20rem)]">
          <div className="grid gap-3 sm:grid-cols-2">
            {questions.map((q) => (
              <div key={q.key} className="text-[13px]">
                <label htmlFor={`${id}-${q.key}`} className="font-medium text-gray-800">{q.prompt.trim() || q.key}</label>
                <AnswerInput q={q} id={`${id}-${q.key}`} value={sample[q.key] ?? ''} onChange={(v) => setSample({ ...sample, [q.key]: v })} />
              </div>
            ))}
          </div>
          <div role="status" aria-live="polite" className={`rounded-lg border-2 px-3 py-3 ${meta.ring}`} data-testid="try-it-result">
            {touched ? (
              <>
                <div className={`inline-flex items-center gap-2 text-lg font-semibold tracking-tight ${meta.text}`}>
                  <span className={`h-2.5 w-2.5 rounded-full ${meta.dot}`} aria-hidden />
                  {meta.label}
                </div>
                <div className="mt-0.5 text-[13px] text-gray-700 tabular-nums">Score {result.score}{result.max > 0 ? ` / ${result.max}` : ''}</div>
                <ul className="mt-2 list-disc space-y-0.5 pl-5 text-[12px] text-gray-800">
                  {result.reasons.map((r, i) => <li key={i}>{r}</li>)}
                </ul>
              </>
            ) : (
              <p className="text-[13px] text-muted">Fill in an answer to see the result.</p>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
